import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FolderGit2, Pause, Play, Trash2, Workflow } from "lucide-react";
import { useProcess, useProcessReadiness, useProcessVersions } from "@/api/queries/processes";
import { useInstantiators, useProcessInstances } from "@/api/queries/processInstances";
import { useUserContext, darfVorgang } from "@/auth/operations";
import { useActivateProcess, useDeleteProcess, usePauseProcess, useReleaseProcess } from "@/api/mutations/processes";
import { useInstantiateProcess, useSetInstantiators } from "@/api/mutations/processInstances";
import { useUsers } from "@/api/queries/users";
import { ApiError } from "@/api/client";
import { BackLink, DetailHeader } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EntityLink } from "@/components/EntityLink";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProcessStatusBadge } from "./List";
import { GapRow, InstanceStatusBadge } from "@/components/ProcessBits";
import { ProcessStatsCard } from "@/components/process-stats/ProcessStatsCard";
import { ProcessSpecCard } from "@/components/process-spec/ProcessSpecCard";
import { SetProcessOwnerDialog } from "@/components/process-actions/SetProcessOwnerDialog";
import type { ProcessReadinessGapView } from "@/types";

// #282 (UC-6): Detail = Eigenschaften + Bereitschaft + Aktivieren/Pausieren.
// Ehrlichkeit gegenüber dem Admin: Aktivieren schaltet NUR den Registry-Status.

export function ProcessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: process, isLoading, isError } = useProcess(id);
  const users = useUsers();
  const activate = useActivateProcess();
  const release = useReleaseProcess();
  const pause = usePauseProcess();
  const del = useDeleteProcess();

  const instances = useProcessInstances(id);
  const instantiators = useInstantiators(id);
  const instantiate = useInstantiateProcess();
  const setInstantiators = useSetInstantiators();
  const navigateAfterCreate = navigate;

  const versions = useProcessVersions(id);
  // Auswahl des Admins; leer = Vorschlag aus der Versions-Sicht (Spec-Version, sonst neuester Tag).
  const [versionChoice, setVersionChoice] = useState("");
  const [checkEnabled, setCheckEnabled] = useState(false);
  const [instantiatorPick, setInstantiatorPick] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  // #303 (UC-2): der Verantwortlichen-Wechsel — die API kann es seit #282, die Oberfläche
  // zeigte ihn bisher nur an.
  const [ownerOpen, setOwnerOpen] = useState(false);
  // 409-Gaps der letzten Aktivierung (server-seitige Neu-Prüfung schlug fehl → anzeigen)
  const [activationGaps, setActivationGaps] = useState<ProcessReadinessGapView[] | null>(null);

  const v = versions.data;
  const suggested = v ? (v.specVersionTagged && v.specVersion ? v.specVersion : (v.activeVersion ?? v.tags[0] ?? "")) : "";
  const version = versionChoice || suggested;
  const readiness = useProcessReadiness(id, version.trim() || undefined, checkEnabled);
  const userMap = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u] as const)), [users.data]);
  const ctx = useUserContext();
  // M5 (Review): Werkbank-Links nur, wenn der Vorgang erlaubt ist (Server-Tabelle #299).
  const darfRepositories = darfVorgang(ctx.data, "repositories.view");
  // #300: Sektionen nur, wenn die Server-Vorgänge sie erlauben (kein role==="admin" im Client).
  const darfInstanzen = darfVorgang(ctx.data, "process-instance.view");
  const darfManage = darfVorgang(ctx.data, "process.manage");
  // LOW-7 (#299-Regel): Aktivieren/Freigeben nur zeigen, wenn der Vorgang erlaubt ist.
  const darfActivate = darfVorgang(ctx.data, "process.activate");

  if (isLoading && !process) {
    return (
      <div className="space-y-4">
        <BackLink to="/processes" label="Prozesse" />
        <p className="text-[13px] text-text3">Lade…</p>
      </div>
    );
  }
  if (isError || !process) {
    return (
      <div className="space-y-4">
        <BackLink to="/processes" label="Prozesse" />
        <p className="text-[13px] text-danger">Prozess nicht gefunden.</p>
      </div>
    );
  }

  const doActivate = () => {
    const v = version.trim();
    if (!v) {
      toast.error("Keine freigegebene Version vorhanden — erst freigeben");
      return;
    }
    setActivationGaps(null);
    activate.mutate(
      { id: process.id, version: v },
      {
        onSuccess: () => toast.success(`Version ${v} aktiviert`),
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            const body = err.body as { code?: string; gaps?: ProcessReadinessGapView[] } | undefined;
            if (body?.code === "PROCESS_NOT_READY" && body.gaps) {
              setActivationGaps(body.gaps);
              toast.error("Nicht bereit — Lücken unten aufgeführt");
              return;
            }
          }
          if (err instanceof ApiError && err.status === 404) {
            toast.error("Version (Tag) nicht gefunden");
            return;
          }
          if (err instanceof ApiError && err.status === 403) {
            toast.error("Keine Berechtigung für diesen Prozess");
            return;
          }
          toast.error("Aktivierung fehlgeschlagen");
        },
      },
    );
  };

  // Nachtrag 2026-08-22 (Arbens Befund): der Tag-Schritt fehlte — "Freigeben & aktivieren"
  // lässt den Master die Version aus der process.json taggen und aktiviert sie dann.
  const doReleaseAndActivate = () => {
    if (!process) return;
    setActivationGaps(null);
    release.mutate(process.id, {
      onSuccess: (r) => {
        setVersionChoice(r.version);
        activate.mutate(
          { id: process.id, version: r.version },
          {
            onSuccess: () => toast.success(`Version ${r.version} freigegeben und aktiviert`),
            onError: (err) => {
              if (err instanceof ApiError && err.status === 409) {
                const body = err.body as { code?: string; gaps?: ProcessReadinessGapView[] } | undefined;
                if (body?.code === "PROCESS_NOT_READY" && body.gaps) {
                  setActivationGaps(body.gaps);
                  toast.error("Version freigegeben, aber nicht bereit — Lücken unten aufgeführt");
                  return;
                }
              }
              toast.error("Version freigegeben — Aktivierung fehlgeschlagen");
            },
          },
        );
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          toast.error("Version bereits vergeben — im Inhalt die Version erhöhen");
          return;
        }
        if (err instanceof ApiError && err.status === 422) {
          toast.error("process.json ungültig — im Inhalt korrigieren");
          return;
        }
        toast.error("Freigabe fehlgeschlagen");
      },
    });
  };

  const gaps = activationGaps ?? readiness.data?.gaps ?? null;
  const ready = activationGaps ? false : readiness.data?.ready;

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/processes"
        backLabel="Prozesse"
        icon={Workflow}
        title={process.name}
        badge={<ProcessStatusBadge status={process.status} />}
        sub={process.description || process.id}
        actions={
          <div className="flex items-center gap-2">
            {darfRepositories && (
              <Button variant="outline" asChild>
                <Link to={`/repositories?repo=${encodeURIComponent(process.managedRepoId ?? `prc-${process.id}`)}`}>
                  <FolderGit2 className="h-4 w-4" />
                  Inhalt bearbeiten
                </Link>
              </Button>
            )}
            {process.status === "active" ? (
              <Button
                variant="outline"
                onClick={() =>
                  pause.mutate(process.id, {
                    onSuccess: () => toast.success("Pausiert"),
                    onError: () => toast.error("Pausieren fehlgeschlagen"),
                  })
                }
                disabled={pause.isPending}
              >
                <Pause className="h-4 w-4" />
                Pausieren
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setDeleteOpen(true)} data-testid="action-delete-process">
                <Trash2 className="h-4 w-4" />
                Löschen
              </Button>
            )}
          </div>
        }
      />

      <PropGrid
        rows={[
          [
            "Verantwortlich",
            <span key="o" className="flex flex-wrap items-center gap-2">
              <EntityLink
                to={`/users/${process.ownerId}`}
                id={process.ownerId}
                label={userMap.get(process.ownerId)?.email}
                isResolving={users.isLoading || users.isError}
              />
              {darfManage && (
                <button
                  type="button"
                  onClick={() => setOwnerOpen(true)}
                  className="text-[12px] text-accent hover:underline"
                  data-testid="process-owner-change"
                >
                  übergeben
                </button>
              )}
            </span>,
          ],
          [
            "Repository",
            darfRepositories ? (
              <Link
                key="r"
                to={`/repositories?repo=${encodeURIComponent(process.managedRepoId ?? `prc-${process.id}`)}`}
                className="font-mono text-[12.5px] text-accent hover:underline"
              >
                {process.repoName}
              </Link>
            ) : (
              <span key="r" className="font-mono text-[12.5px]">{process.repoName}</span>
            ),
          ],
          // #303 (UC-2): hiess „Live-Branch" — Git-Vokabular in einer Fach-Oberfläche
          // (#293-Regel). Der Wert bleibt derselbe technische Name, die ZEILE sagt jetzt,
          // was er bedeutet: der freigegebene, gerade gültige Stand.
          ["Live-Stand", <span key="b" className="font-mono text-[12.5px]">{process.branch}</span>],
          [
            "Aktive Version",
            process.activation ? (
              <span key="v" className="font-mono text-[12.5px]">
                {process.activation.version} · {process.activation.commitSha.slice(0, 10)}
              </span>
            ) : (
              "—"
            ),
          ],
          [
            "Aktiviert",
            process.activation
              ? `${new Date(process.activation.activatedAt).toLocaleString("de-CH")} von ${
                  userMap.get(process.activation.activatedBy)?.email ?? process.activation.activatedBy
                }`
              : "—",
          ],
          ["Angelegt", new Date(process.createdAt).toLocaleString("de-CH")],
        ]}
      />

      {/* #303 (UC-2): „läuft mein Prozess?" — blendet sich still aus, wenn der Master die
          Kennzahlen nicht herausgibt (fremde Vorgänge, 404 statt 403). */}
      <ProcessStatsCard processId={process.id} />

      {/* #326 (UC-1): die sechs Abschnitte, die eine Vorlage ausmachen — vor der
          Bereitschaft, weil man zuerst festlegt, WAS der Prozess ist, und erst danach
          prüft, ob er laufen kann. Blendet sich still aus, wenn der Server die Ablage
          dieses Prozesses gar nicht zum Lesen freigibt. */}
      <ProcessSpecCard process={process} />

      <SectionCard title="Bereitschaft & Aktivierung">
        <div className="space-y-4">
          {versions.isError && (
            <p className="text-[12.5px] text-danger" data-testid="process-versions-error">
              Versionen konnten nicht gelesen werden — Ablage nicht erreichbar. Später erneut versuchen.
            </p>
          )}
          {v && v.specDirty && (
            <p className="text-[12.5px] text-warn" data-testid="process-spec-dirty">
              Der Inhalt weicht von der freigegebenen Version {v.specVersion} ab — für eine neue
              Freigabe die Versionsnummer in der process.json erhöhen.
            </p>
          )}
          {darfActivate && v && v.specVersion && !v.specVersionTagged && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface2 px-3.5 py-2.5">
              <p className="text-[12.5px] text-text2">
                Der Inhalt steht auf Version <span className="font-semibold">{v.specVersion}</span> — noch
                nicht freigegeben.
              </p>
              <Button
                onClick={doReleaseAndActivate}
                disabled={release.isPending || activate.isPending}
                data-testid="process-release-activate"
              >
                <Play className="h-4 w-4" />
                {release.isPending || activate.isPending
                  ? "Gebe frei…"
                  : `Version ${v.specVersion} freigeben & aktivieren`}
              </Button>
            </div>
          )}
          {v && v.specVersion === null && (
            <p className="text-[12.5px] text-danger">
              Die process.json ist nicht lesbar oder ungültig — unter „Inhalt bearbeiten" korrigieren.
            </p>
          )}
          <div className="flex items-end gap-2">
            <div className="w-56 space-y-1.5">
              <Label htmlFor="process-version">Version</Label>
              <select
                id="process-version"
                className="h-9 w-full rounded-md border border-border bg-surface px-3 text-[13px]"
                value={version}
                onChange={(e) => {
                  setVersionChoice(e.target.value);
                  setCheckEnabled(false);
                  setActivationGaps(null);
                }}
                disabled={!v || v.tags.length === 0}
                data-testid="process-version-select"
              >
                {(!v || v.tags.length === 0) && <option value="">Noch keine freigegebene Version</option>}
                {(v?.tags ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                    {v?.activeVersion === t ? " (aktiv)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setActivationGaps(null);
                setCheckEnabled(true);
                void readiness.refetch();
              }}
              disabled={readiness.isFetching}
              data-testid="process-check-readiness"
            >
              {readiness.isFetching ? "Prüfe…" : "Bereitschaft prüfen"}
            </Button>
            {darfActivate && (
              <Button
                onClick={doActivate}
                disabled={activate.isPending || !version.trim()}
                data-testid="process-activate"
              >
                <Play className="h-4 w-4" />
                {activate.isPending ? "Aktiviere…" : "Aktivieren"}
              </Button>
            )}
          </div>

          {readiness.isError && checkEnabled && (
            <p className="text-[12.5px] text-danger">
              Prüfung fehlgeschlagen
              {(readiness.error as ApiError | undefined)?.status === 404 ? " — Version (Tag) nicht gefunden" : ""}.
            </p>
          )}

          {gaps && (
            <div className="rounded-lg border border-border bg-surface2 px-3.5 py-2">
              {ready === true && gaps.length === 0 ? (
                <div className="flex items-center gap-2 py-1.5 text-[12.5px] font-semibold text-ok">
                  <CheckCircle2 className="h-4 w-4" /> Bereit — alle Voraussetzungen stehen.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {gaps.map((g, i) => (
                    <GapRow key={i} gap={g} />
                  ))}
                  {ready === true && gaps.length > 0 && (
                    <div className="flex items-center gap-2 py-1.5 text-[12.5px] text-ok">
                      <CheckCircle2 className="h-4 w-4" /> Bereit (nur Hinweise, keine Blocker).
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-[12px] leading-relaxed text-text3">
            „Freigeben" erstellt aus dem aktuellen Inhalt eine unveränderliche Version (die
            Versionsnummer steht in der process.json). Aktivieren markiert eine freigegebene
            Version als gültig — ausgeführt wird noch nichts, die Lauf-Engine folgt separat.
          </p>
        </div>
      </SectionCard>

      {darfInstanzen && (
        <SectionCard
          title={`Instanzen (${(instances.data ?? []).length})`}
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                instantiate.mutate(
                  { templateId: process.id },
                  {
                    onSuccess: (created) => navigateAfterCreate(`/process-instances/${created.id}`),
                    onError: (err) => {
                      const conflict = err instanceof ApiError && err.status === 409;
                      toast.error(conflict ? "Template ist nicht aktiv" : "Anlegen fehlgeschlagen");
                    },
                  },
                )
              }
              disabled={instantiate.isPending || process.status !== "active"}
              data-testid="process-add-instance"
            >
              + Instanz anlegen
            </Button>
          }
        >
          {(instances.data ?? []).length === 0 ? (
            <p className="text-[12.5px] text-text3">
              Noch keine Instanzen — eine Instanz bindet Agent + Auslöser an dieses Template.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Inhaber</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Auslöser</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Lücken</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(instances.data ?? []).map((i) => (
                  <TableRow
                    key={i.id}
                    className="cursor-pointer"
                    onClick={() => navigateAfterCreate(`/process-instances/${i.id}`)}
                    data-testid={`instance-row-${i.id}`}
                  >
                    <TableCell>
                      <EntityLink
                        to={`/users/${i.ownerId}`}
                        id={i.ownerId}
                        label={userMap.get(i.ownerId)?.email}
                        isResolving={users.isLoading || users.isError}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-[12px]">{i.agentId ?? "—"}</TableCell>
                    <TableCell className="text-[12px]">{i.triggerKind ?? "—"}</TableCell>
                    <TableCell>
                      <InstanceStatusBadge status={i.status} />
                    </TableCell>
                    <TableCell className="text-[12px]">
                      {i.blockCount > 0 ? `${i.blockCount} offen` : i.gapCount > 0 ? `${i.gapCount} Hinweise` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      )}

      {darfManage && (
        <SectionCard title="Instanziierungs-Berechtigungen">
          <div className="space-y-3">
            <p className="text-[12px] text-text3">
              Wer darf dieses Template für sich einrichten? Deklarierte Fach-Rollen wirken
              zusätzlich (bei der Aktivierung übernommen
              {process.instantiationRoles && process.instantiationRoles.length > 0
                ? `: ${process.instantiationRoles.join(", ")}`
                : " — aktuell keine"}
              ). Ohne beides: nur die verantwortliche Person + Admin.
            </p>
            {(instantiators.data ?? []).length > 0 && (
              <div className="divide-y divide-border">
                {(instantiators.data ?? []).map((g) => (
                  <div key={g.userId} className="flex items-center justify-between py-1.5">
                    <EntityLink
                      to={`/users/${g.userId}`}
                      id={g.userId}
                      label={userMap.get(g.userId)?.email}
                      isResolving={users.isLoading || users.isError}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setInstantiators.mutate(
                          {
                            processId: process.id,
                            userIds: (instantiators.data ?? []).filter((x) => x.userId !== g.userId).map((x) => x.userId),
                          },
                          {
                            onSuccess: () => toast.success("Zuweisung entfernt"),
                            onError: () => toast.error("Entfernen fehlgeschlagen"),
                          },
                        )
                      }
                      data-testid={`instantiator-remove-${g.userId}`}
                    >
                      Entfernen
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="w-72 space-y-1.5">
                <Label>User zuweisen</Label>
                <Select value={instantiatorPick || "__none"} onValueChange={(v) => setInstantiatorPick(v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="instantiator-pick">
                    <SelectValue placeholder="User wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— wählen —</SelectItem>
                    {(users.data ?? [])
                      .filter((u) => !(instantiators.data ?? []).some((g) => g.userId === u.id))
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  if (!instantiatorPick) return;
                  setInstantiators.mutate(
                    {
                      processId: process.id,
                      userIds: [...(instantiators.data ?? []).map((g) => g.userId), instantiatorPick],
                    },
                    {
                      onSuccess: () => {
                        setInstantiatorPick("");
                        toast.success("Zugewiesen");
                      },
                      onError: () => toast.error("Zuweisen fehlgeschlagen"),
                    },
                  );
                }}
                disabled={!instantiatorPick || setInstantiators.isPending}
                data-testid="instantiator-add"
              >
                Zuweisen
              </Button>
            </div>
          </div>
        </SectionCard>
      )}

      {ownerOpen && (
        <SetProcessOwnerDialog
          open
          onOpenChange={setOwnerOpen}
          processId={process.id}
          processName={process.name}
          currentOwnerId={process.ownerId}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Prozess löschen?"
        description={`Entfernt „${process.name}" aus der Registry. Das Repository bleibt als Historie bestehen.`}
        confirmText="Löschen"
        confirmVariant="destructive"
        onConfirm={() =>
          del.mutate(process.id, {
            onSuccess: () => {
              toast.success("Prozess gelöscht");
              navigate("/processes");
            },
            onError: (err) => {
              const body = err instanceof ApiError ? (err.body as { code?: string } | undefined) : undefined;
              toast.error(
                body?.code === "PROCESS_HAS_INSTANCES"
                  ? "Es existieren noch Instanzen — bitte zuerst die Instanzen löschen"
                  : err instanceof ApiError && err.status === 409
                    ? "Aktive Prozesse können nicht gelöscht werden — erst pausieren"
                    : "Löschen fehlgeschlagen",
              );
            },
          })
        }
      />
    </div>
  );
}
